import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Edit, Trash2, X, Upload, User, Mail, Users, UsersRound, Key } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import tokenStore from '../services/tokenStore';
import { ActionTile } from '../components/ui/ActionTile';
import CrewFilter from '../components/CrewFilter';
import useCloudSync from '../hooks/useCloudSync';
import {
  listCrews,
  getCrewName,
  getCrewColor,
  getActiveCrewFilter,
} from '../services/CrewService';
import Crews from './Crews';
import { IconTile, TINT } from '../components/ui/IconTile';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Team endpoints are authenticated + workspace-scoped on the backend, so every
// call must carry the in-memory access token (and the httpOnly cookie).
const authCfg = () => ({
  headers: { Authorization: `Bearer ${tokenStore.getAccessToken() || ''}` },
  withCredentials: true,
});

const EmployeeManagement = () => {
  // ?tab=crews deep-links the Crews sub-view so the legacy /crews route + sidebar shortcut still resolve.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'crews' ? 'crews' : 'employees';
  const [activeTab, setActiveTab] = useState(initialTab);
  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab === 'crews') setSearchParams({ tab: 'crews' }, { replace: true });
    else setSearchParams({}, { replace: true });
  };

  const [employees, setEmployees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  // Set of normalized emails who have ACTUALLY joined the team workspace.
  // Used to render "Joined" vs "Invite pending" badges per employee.
  const [memberEmails, setMemberEmails] = useState(new Set());
  const [workspaceActive, setWorkspaceActive] = useState(
    Boolean(localStorage.getItem('maidly_workspace_id'))
  );
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'employee',
    hourlyRate: '',
    username: '',
    password: '',
    status: 'active',
    photo: '',
    crewId: ''
  });
  // Cached crews + the currently selected crew filter (synced via the
  // `maidly:crew-filter-changed` window event from CrewFilter).
  const [crews, setCrews] = useState(listCrews());
  const [crewFilter, setCrewFilter] = useState(getActiveCrewFilter());
  useEffect(() => {
    const onFilter = (e) => setCrewFilter(e.detail?.crewId || '');
    const onStorage = () => setCrews(listCrews());
    window.addEventListener('maidly:crew-filter-changed', onFilter);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('maidly:crew-filter-changed', onFilter);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    loadEmployees();
    refreshTeamMembers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live refresh when another device pushes employee changes
  useCloudSync(() => {
    const data = JSON.parse(localStorage.getItem('maidly_employees') || '[]');
    setEmployees(data);
    refreshTeamMembers();
  }, ['employees']);

  // Refresh team membership when an invite is sent or an employee deleted,
  // so the badge state is always current.
  const refreshTeamMembers = async () => {
    const workspaceId = localStorage.getItem('maidly_workspace_id');
    setWorkspaceActive(Boolean(workspaceId));
    if (!workspaceId) {
      setMemberEmails(new Set());
      return;
    }
    try {
      const resp = await axios.get(
        `${API_URL}/api/team/members/${encodeURIComponent(workspaceId)}`,
        authCfg()
      );
      if (resp.data?.success) {
        // Normalize to lowercase defensively in case the backend ever returns
        // mixed-case emails — keeps the Set.has() check case-insensitive.
        setMemberEmails(
          new Set((resp.data.member_emails || []).map((e) => (e || '').toLowerCase()))
        );
      }
    } catch (e) {
      console.warn('[EmployeeMgmt] Failed to load team members:', e?.message || e);
    }
  };

  const loadEmployees = () => {
    let data = JSON.parse(localStorage.getItem('maidly_employees') || '[]');

    // Self-heal: make sure the currently logged-in user (owner/admin/manager)
    // always appears in their own roster. They are only added to
    // `maidly_employees` at registration, and logout clears that cache — so on
    // a later login (or a fresh device) their own row would be missing here.
    // Deduped by id AND email so it never creates a duplicate; the setItem is
    // wrapped by AutoSync, so the record also syncs to the cloud + other devices.
    try {
      const me = JSON.parse(localStorage.getItem('maidly_current_user') || 'null');
      if (me && me.id) {
        const emailLc = (me.email || '').toLowerCase();
        const exists = data.some(
          (e) => e?.id === me.id || (emailLc && (e?.email || '').toLowerCase() === emailLc)
        );
        if (!exists) {
          const selfRecord = {
            id: me.id,
            name: me.name || me.email || 'Me',
            email: me.email || '',
            phone: '',
            role: me.role || 'employee',
            hourlyRate: 0,
            username: me.email || '',
            status: 'active',
            photo: '',
            createdAt: new Date().toISOString(),
          };
          data = [...data, selfRecord];
          localStorage.setItem('maidly_employees', JSON.stringify(data));
        }
      }
    } catch (e) {
      console.warn('[EmployeeMgmt] self-heal current user failed:', e);
    }

    setEmployees(data);
    
    // Sync all employees to backend on load (ensures auth works)
    if (data.length > 0) {
      syncAllEmployeesToBackend(data);
    }
  };

  // Sync all employees to backend (without passwords - just creates records if missing)
  const syncAllEmployeesToBackend = async (employeeList) => {
    try {
      const employeesToSync = employeeList.map(emp => ({
        id: emp.id,
        name: emp.name,
        email: emp.email,
        username: emp.username,
        password: emp.password, // Include password for initial sync
        role: emp.role,
        phone: emp.phone,
        status: emp.status
      }));
      
      await axios.post(`${API_URL}/api/auth/sync-employees`, {
        employees: employeesToSync
      });
      console.log('All employees synced to backend');
    } catch (error) {
      console.error('Failed to sync employees to backend:', error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.password && !editingEmployee) {
      toast.error('Password is required');
      return;
    }

    const existingEmployees = JSON.parse(localStorage.getItem('maidly_employees') || '[]');
    const emailExists = existingEmployees.some(emp => 
      emp.email === formData.email && emp.id !== editingEmployee?.id
    );

    if (emailExists) {
      toast.error('Email already exists');
      return;
    }

    let updatedEmployee = null;

    if (editingEmployee) {
      updatedEmployee = { ...editingEmployee, ...formData, password: formData.password || editingEmployee.password };
      const updated = employees.map(emp => 
        emp.id === editingEmployee.id ? updatedEmployee : emp
      );
      setEmployees(updated);
      localStorage.setItem('maidly_employees', JSON.stringify(updated));
      toast.success('Employee updated successfully');
    } else {
      updatedEmployee = {
        ...formData,
        id: Date.now().toString(),
        hourlyRate: parseFloat(formData.hourlyRate),
        createdAt: new Date().toISOString()
      };
      const updated = [...employees, updatedEmployee];
      setEmployees(updated);
      localStorage.setItem('maidly_employees', JSON.stringify(updated));
      toast.success('Employee added successfully');
    }

    // Sync employee to backend for login authentication
    if (updatedEmployee && (formData.password || !editingEmployee)) {
      syncEmployeeToBackend(updatedEmployee, formData.password);
    }

    // Pre-approve this email so they auto-join the team workspace on sign-up
    if (updatedEmployee?.email) {
      registerTeamInvite(updatedEmployee);
    }

    resetForm();
  };

  // If we are in a team workspace, register the employee's email as a pre-approved
  // member. When they later sign up with that email, the Register flow will
  // silently auto-join them to this workspace and assign their role.
  const registerTeamInvite = async (employee) => {
    try {
      const workspaceId = localStorage.getItem('maidly_workspace_id');
      if (!workspaceId) return; // No team active, nothing to pre-approve
      await axios.post(`${API_URL}/api/team/invite`, {
        email: employee.email,
        workspace_id: workspaceId,
        role: employee.role || 'employee',
        name: employee.name,
        invited_by: JSON.parse(localStorage.getItem('maidly_current_user') || '{}')?.id || null,
      }, authCfg());
    } catch (error) {
      console.warn('[EmployeeMgmt] Failed to register team invite:', error?.message || error);
    }
  };

  // Sync employee to backend users database
  const syncEmployeeToBackend = async (employee, password) => {
    try {
      await axios.post(`${API_URL}/api/auth/sync-employees`, {
        employees: [{
          id: employee.id,
          name: employee.name,
          email: employee.email,
          username: employee.username,
          password: password, // Only send if password was changed/set
          role: employee.role,
          phone: employee.phone,
          status: employee.status
        }]
      });
      console.log('Employee synced to backend for authentication');
    } catch (error) {
      console.error('Failed to sync employee to backend:', error);
      // Don't show error to user - localStorage is primary storage
    }
  };

  const handleDelete = (id) => {
    const employee = employees.find(e => e.id === id);
    if (employee.role === 'admin') {
      const adminCount = employees.filter(e => e.role === 'admin').length;
      if (adminCount === 1) {
        toast.error('Cannot delete the last admin');
        return;
      }
    }

    if (window.confirm('Are you sure you want to delete this employee?')) {
      const updated = employees.filter(emp => emp.id !== id);
      setEmployees(updated);
      localStorage.setItem('maidly_employees', JSON.stringify(updated));
      toast.success('Employee deleted successfully');

      // Revoke their pre-approved team invite (if we're in a workspace)
      try {
        const workspaceId = localStorage.getItem('maidly_workspace_id');
        if (workspaceId && employee?.email) {
          axios
            .delete(`${API_URL}/api/team/invite/${encodeURIComponent(workspaceId)}/${encodeURIComponent(employee.email)}`, authCfg())
            .then(() => refreshTeamMembers())
            .catch((e) => console.warn('[EmployeeMgmt] Failed to revoke invite:', e?.message || e));
        }
      } catch (e) {
        console.warn('[EmployeeMgmt] Invite revoke error:', e);
      }
    }
  };

  // Send a magic-link email invitation to an employee. They click the link
  // in the email and sign up — auto-joined to the team with the role we set.
  const handleSendInvite = async (employee) => {
    if (!employee?.email) {
      toast.error('Employee has no email on file');
      return;
    }
    const workspaceId = localStorage.getItem('maidly_workspace_id');
    if (!workspaceId) {
      toast.error('No team workspace is configured yet');
      return;
    }
    try {
      const settings = JSON.parse(localStorage.getItem('maidly_settings') || '{}');
      const currentUser = JSON.parse(localStorage.getItem('maidly_current_user') || '{}');
      const resp = await axios.post(`${API_URL}/api/team/send-invite`, {
        email: employee.email,
        workspace_id: workspaceId,
        role: employee.role || 'employee',
        name: employee.name,
        invited_by_name: currentUser.name,
        invited_by_email: currentUser.email,
        business_name: settings?.companyInfo?.name,
      }, authCfg());
      if (resp.data?.sent) {
        toast.success(`Invitation sent to ${employee.email}`);
        // Refresh badge state — owner sees this person stays "Pending" until they accept
        refreshTeamMembers();
      } else if (resp.data?.invite_link) {
        // Email failed but link generated — copy to clipboard so owner can share
        navigator.clipboard?.writeText(resp.data.invite_link);
        toast.success('Email failed — invite link copied to clipboard');
      } else {
        toast.error(resp.data?.error || 'Could not send invitation');
      }
    } catch (e) {
      toast.error('Failed to send invitation');
      console.error('[EmployeeMgmt] send-invite error:', e);
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      email: employee.email,
      phone: employee.phone,
      role: employee.role,
      hourlyRate: employee.hourlyRate.toString(),
      username: employee.username || '',
      password: '',
      status: employee.status,
      photo: employee.photo || '',
      crewId: employee.crewId || ''
    });
    setPhotoPreview(employee.photo || '');
    setShowModal(true);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 1MB)
    if (file.size > 1 * 1024 * 1024) {
      toast.error('Image size should be less than 1MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setPhotoPreview(base64String);
      setFormData({ ...formData, photo: base64String });
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setPhotoPreview('');
    setFormData({ ...formData, photo: '' });
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingEmployee(null);
    setPhotoPreview('');
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'employee',
      hourlyRate: '',
      username: '',
      password: '',
      status: 'active',
      photo: '',
      crewId: ''
    });
  };

  return (
    <div data-testid="employee-management">
      {/* Tabs: Employees ↔ Crews (combined view) */}
      <div
        role="tablist"
        aria-label="Team views"
        style={{
          display: 'inline-flex', gap: 6, padding: 5, marginBottom: 16,
          background: '#f1f5f9', borderRadius: 12, border: '1px solid #e2e8f0',
          boxShadow: 'inset 0 1px 2px rgba(15,23,42,0.04)',
        }}
      >
        {[
          { id: 'employees', label: 'Employees', icon: Users, tint: TINT.employees },
          { id: 'crews', label: 'Crews', icon: UsersRound, tint: TINT.recurring },
        ].map((t) => {
          const Icon = t.icon;
          const isActive = activeTab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              data-testid={`employees-tab-${t.id}`}
              onClick={() => switchTab(t.id)}
              style={{
                all: 'unset', cursor: 'pointer',
                padding: '8px 18px 8px 8px', borderRadius: 9,
                fontSize: 13, fontWeight: 700, letterSpacing: 0.1,
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: isActive ? '#0073ea' : 'transparent',
                color: isActive ? '#ffffff' : '#475569',
                boxShadow: isActive ? '0 3px 10px rgba(0,115,234,0.28)' : 'none',
                transition: 'background 0.18s, color 0.18s, box-shadow 0.18s',
              }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(15,23,42,0.05)'; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
            >
              <IconTile icon={Icon} tint={isActive ? 'rgba(255,255,255,0.22)' : t.tint} size={24} iconSize={13} />
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'crews' ? (
        <Crews />
      ) : (
      <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <IconTile icon={Users} tint={TINT.employees} size={36} />
          Employee Management
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <CrewFilter />
          <button
            onClick={() => setShowModal(true)}
            data-testid="add-employee-button"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 1rem 0.55rem 0.55rem',
              background: TINT.employees, color: '#fff',
              border: 'none', borderRadius: 12,
              cursor: 'pointer',
              fontSize: '0.9rem', fontWeight: 700,
              boxShadow: `0 6px 16px ${TINT.employees}55`,
              transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
              letterSpacing: 0.1,
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#9333ea';
              e.currentTarget.style.boxShadow = `0 10px 22px ${TINT.employees}88`;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = TINT.employees;
              e.currentTarget.style.boxShadow = `0 6px 16px ${TINT.employees}55`;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <IconTile icon={Plus} tint="rgba(255,255,255,0.22)" size={30} />
            Add Employee
          </button>
        </div>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Photo</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Crew</th>
              <th>Hourly Rate</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees
              .filter((e) => !crewFilter || e.crewId === crewFilter)
              .map(employee => (
              <tr key={employee.id} data-testid={`employee-row-${employee.id}`}>
                <td>
                  {employee.photo ? (
                    <img 
                      src={employee.photo} 
                      alt={employee.name}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2px solid #e5e7eb'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #e5e7eb'
                    }}>
                      <User size={20} color="#9ca3af" />
                    </div>
                  )}
                </td>
                <td>{employee.name}</td>
                <td>
                  <span>{employee.email}</span>
                </td>
                <td>{employee.phone}</td>
                <td>
                  {(() => {
                    const role = employee.role || 'employee';
                    const roleConfig = {
                      admin:    { bg: '#a855f7', label: 'Admin',    Icon: Key },
                      owner:    { bg: '#22c55e', label: 'Owner',    Icon: Key },
                      manager:  { bg: '#0073ea', label: 'Manager',  Icon: UsersRound },
                      employee: { bg: '#64748b', label: 'Employee', Icon: User },
                    }[role] || { bg: '#64748b', label: 'Employee', Icon: User };
                    const Icon = roleConfig.Icon;
                    return (
                      <span
                        data-testid={`role-pill-${employee.id}`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.4rem',
                          padding: '0.25rem 0.7rem 0.25rem 0.3rem',
                          borderRadius: 999,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          background: `${roleConfig.bg}14`,
                          color: roleConfig.bg,
                          border: `1px solid ${roleConfig.bg}33`,
                        }}
                      >
                        <span style={{
                          width: 18, height: 18, borderRadius: 6,
                          background: roleConfig.bg, color: '#fff',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Icon size={11} strokeWidth={2.5} />
                        </span>
                        {roleConfig.label}
                      </span>
                    );
                  })()}
                </td>
                <td data-testid={`crew-cell-${employee.id}`}>
                  {employee.crewId ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        padding: '0.2rem 0.6rem',
                        borderRadius: 999,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: `${getCrewColor(employee.crewId)}1a`,
                        color: getCrewColor(employee.crewId),
                        border: `1px solid ${getCrewColor(employee.crewId)}40`,
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: getCrewColor(employee.crewId),
                        }}
                      />
                      {getCrewName(employee.crewId) || 'Crew'}
                    </span>
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</span>
                  )}
                </td>
                <td>${employee.hourlyRate}/hr</td>
                <td>
                  <span className={`badge ${employee.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                    {employee.status}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <ActionTile
                      onClick={() => handleSendInvite(employee)}
                      testid={`invite-employee-${employee.id}`}
                      title="Email invitation link"
                      icon={Mail}
                      tint={TINT.expenses}
                    />
                    <ActionTile
                      onClick={() => handleEdit(employee)}
                      testid={`edit-employee-${employee.id}`}
                      title="Edit employee"
                      icon={Edit}
                      tint={TINT.timeClock}
                    />
                    <ActionTile
                      onClick={() => handleDelete(employee.id)}
                      testid={`delete-employee-${employee.id}`}
                      title="Delete employee"
                      icon={Trash2}
                      tint={TINT.expenses}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1.5rem',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                {editingEmployee ? 'Edit Employee' : 'Add Employee'}
              </h2>
              <button onClick={resetForm} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
              {/* Photo Upload */}
              <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.75rem' }}>Employee Photo</label>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                  {photoPreview ? (
                    <div style={{ position: 'relative' }}>
                      <img
                        src={photoPreview}
                        alt="Preview"
                        style={{
                          width: '120px',
                          height: '120px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '3px solid #0073ea'
                        }}
                      />
                      <button
                        type="button"
                        onClick={removePhoto}
                        style={{
                          position: 'absolute',
                          top: '0',
                          right: '0',
                          background: '#e44258',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ) : (
                    <div style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: '50%',
                      border: '2px dashed #d1d5db',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#f9fafb'
                    }}>
                      <User size={48} color="#9ca3af" />
                    </div>
                  )}
                  
                  <label htmlFor="photo-upload" className="btn btn-secondary" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Upload size={16} />
                    {photoPreview ? 'Change Photo' : 'Upload Photo'}
                  </label>
                  <input
                    id="photo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    style={{ display: 'none' }}
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    PNG, JPG or JPEG. Max 1MB
                  </p>
                </div>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="employee-name-input"
                  required
                  className="input"
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="employee-email-input"
                  required
                  className="input"
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input"
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  data-testid="employee-role-select"
                  required
                  className="input"
                >
                  <option value="employee">Employee (Regular Staff)</option>
                  <option value="manager">Manager (Team Lead / Supervisor)</option>
                  <option value="admin">Admin (Business Owner)</option>
                </select>
                
                {/* Role Description */}
                <div style={{ 
                  marginTop: '0.5rem', 
                  padding: '0.75rem', 
                  background: '#f8fafc', 
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  {formData.role === 'employee' && (
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      <strong style={{ color: '#374151' }}>Employee Access:</strong>
                      <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0 }}>
                        <li>Track own mileage</li>
                        <li>View own data only</li>
                        <li>Cannot see other employees</li>
                      </ul>
                    </div>
                  )}
                  {formData.role === 'manager' && (
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      <strong style={{ color: '#374151' }}>Manager Access:</strong>
                      <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0 }}>
                        <li>✅ Fleet View (see all drivers)</li>
                        <li>✅ View all employee trips</li>
                        <li>✅ Filter by employee</li>
                        <li>✅ Manage employee data</li>
                      </ul>
                    </div>
                  )}
                  {formData.role === 'admin' && (
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      <strong style={{ color: '#374151' }}>Admin Access (Full Control):</strong>
                      <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0 }}>
                        <li>✅ Everything Manager can do</li>
                        <li>✅ System settings</li>
                        <li>✅ Delete employees</li>
                        <li>✅ Full data access</li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Crew</label>
                <select
                  value={formData.crewId}
                  onChange={(e) => setFormData({ ...formData, crewId: e.target.value })}
                  data-testid="employee-crew-select"
                  className="input"
                >
                  <option value="">— No crew —</option>
                  {crews.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {crews.length === 0 && (
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.4rem' }}>
                    Create crews in the Crews page to group employees into sub-teams.
                  </p>
                )}
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Hourly Rate *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.hourlyRate}
                  onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                  required
                  className="input"
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="input"
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                  Password {!editingEmployee && '*'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  data-testid="employee-password-input"
                  required={!editingEmployee}
                  className="input"
                  placeholder={editingEmployee ? 'Leave blank to keep current' : ''}
                />
              </div>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Status *</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  required
                  className="input"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button type="button" onClick={resetForm} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" data-testid="save-employee-button" className="btn btn-primary">
                  {editingEmployee ? 'Update' : 'Add'} Employee
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default EmployeeManagement;
